/**
 * v1.14.61.1.0 — Wizard v2 "Bulk per garanzia" — Frontend principale.
 *
 * Shell con 7 step + state management `bulk_v1`. UX paradigm shift:
 * invece di "Garanzie per immobile" abbiamo "Immobili per garanzia".
 * Riferimenti:
 *   - /app/memory/wizard_v2_data_schema.md
 *   - /app/memory/wizard_v2_tariff_mapping.md
 *   - /app/memory/wizard_v2_plan_micro_steps.md
 */
import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, Save, Loader2, Flame, ShieldAlert,
  Building, CloudRain, Mountain, Sparkles, ListChecks, Check,
} from "lucide-react";
import GaranziaTableStep from "./components/GaranziaTableStep";
// v1.14.61.2.0 — LivePremiumSidebar deprecato sul frontend pubblico
// (broker-gated quote: il cliente non vede premio live).
// import LivePremiumSidebar from "./components/LivePremiumSidebar";
import RiepilogoStep from "./components/RiepilogoStep";
import { WIZARD_LABELS, FIELD_TOOLTIPS } from "../../utils/wizardLabels";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PALETTE = {
  navy: "#0B2545",
  navyDark: "#06182E",
  gold: "#C9A961",
  goldSoft: "#E2C988",
  cream: "#F8F4ED",
  ink: "#1A1A1A",
  text: "#3A3A3A",
  muted: "#6E6E6E",
  border: "#E5DFD4",
};

// ─────────────────────────────────────────────────────────────────────────
// Default state bulk_v1 vuoto
// ─────────────────────────────────────────────────────────────────────────

const emptyBulk = () => ({
  schema_version: "bulk_v1",
  contraente: {
    ragione_sociale: "", partita_iva: "", email: "", telefono: "",
    note: "",
  },
  incendio: {
    immobili_inclusi: [], per_immobile: {},
    sub_coperture_globali: {
      A_beni_speciali: { attiva: false, valore_eur: 0 },
      B_demolizione: { attiva: false, valore_eur: 0 },
      C_ricorso_terzi: { attiva: false },
      D_rischio_locativo_auto: { attiva: false },
      E_fenomeno_elettrico: { attiva: false, valore_eur: 0 },
      F_indennita_aggiuntiva_10pct: { attiva: false },
      M_acqua_piovana: { attiva: false, valore_eur: 10000 },
      N_spargimenti_acqua: { attiva: false },
      V_rottura_lastre: { attiva: false, valore_eur: 0 },
      W_perdita_pigioni: { attiva: false, valore_eur: 0 },
    },
  },
  furto: {
    immobili_inclusi: [], per_immobile: {},
    sub_coperture_per_immobile: {},
    sub_coperture_cpi_rv_per_immobile: {},
  },
  rcf: { immobili_inclusi: [], per_immobile: {} },
  eventi_atmosferici: {
    immobili_inclusi: [], sub_coperture_per_immobile: {},
  },
  eventi_catastrofali: {
    immobili_inclusi: [], sub_coperture_per_immobile: {},
  },
  altre_sub_coperture: {
    immobili_inclusi: [], sub_coperture_per_immobile: {},
  },
});

// ─────────────────────────────────────────────────────────────────────────
// Sub-coperture INCENDIO globali (8 toggleabili — A,B,C,D,E,F,V,W)
// Riferimento: wizard_v2_tariff_mapping.md §Step 1 + wizardLabels.js
// ─────────────────────────────────────────────────────────────────────────

const buildIncendioSubCoperture = () => {
  const L = WIZARD_LABELS.incendio;
  const T = FIELD_TOOLTIPS;
  return [
    { code: "A_beni_speciali", label: L.sub_coperture.A_beni_speciali,
      type: "value_eur", hint: L.sub_hints.A_beni_speciali,
      placeholder: "Valore PRA €", rate_permille: 2.5,
      tooltip: T.A_beni_speciali },
    { code: "B_demolizione", label: L.sub_coperture.B_demolizione,
      type: "value_eur", hint: L.sub_hints.B_demolizione,
      placeholder: "Valore PRA €", rate_permille: 20.0,
      tooltip: T.B_demolizione },
    { code: "C_ricorso_terzi", label: L.sub_coperture.C_ricorso_terzi,
      type: "value_eur", hint: L.sub_hints.C_ricorso_terzi,
      placeholder: "Somma assicurata PRA €",
      tooltip: T.C_ricorso_terzi,
      manualRateInfo: "Tasso applicato dal motore al submit (dipende dal settore)" },
    { code: "D_rischio_locativo_auto",
      label: L.sub_coperture.D_rischio_locativo_auto,
      type: "toggle", hint: L.sub_hints.D_rischio_locativo_auto },
    { code: "E_fenomeno_elettrico",
      label: L.sub_coperture.E_fenomeno_elettrico,
      type: "value_eur", hint: L.sub_hints.E_fenomeno_elettrico,
      placeholder: "Valore PRA €", rate_permille: 15.0,
      tooltip: T.E_fenomeno_elettrico },
    { code: "F_indennita_aggiuntiva_10pct",
      label: L.sub_coperture.F_indennita_aggiuntiva_10pct,
      type: "toggle", hint: L.sub_hints.F_indennita_aggiuntiva_10pct },
    { code: "M_acqua_piovana", label: L.sub_coperture.M_acqua_piovana,
      type: "value_eur", hint: L.sub_hints.M_acqua_piovana,
      placeholder: "Limite indennizzo € (10k/25k/50k)",
      tooltip: T.M_acqua_piovana,
      manualRateInfo: "Premio fisso: €50/100/150 per limite scelto (10k/25k/50k)" },
    { code: "N_spargimenti_acqua",
      label: L.sub_coperture.N_spargimenti_acqua,
      type: "toggle", hint: L.sub_hints.N_spargimenti_acqua,
      tooltip: T.N_spargimenti_acqua },
    { code: "V_rottura_lastre", label: L.sub_coperture.V_rottura_lastre,
      type: "value_eur", hint: L.sub_hints.V_rottura_lastre,
      placeholder: "Valore PRA cristalli €", rate_permille: 15.0,
      tooltip: T.V_rottura_lastre },
    { code: "W_perdita_pigioni", label: L.sub_coperture.W_perdita_pigioni,
      type: "value_eur", hint: L.sub_hints.W_perdita_pigioni,
      placeholder: "Annualità locazione €", rate_permille: 3.0,
      tooltip: T.W_perdita_pigioni },
  ];
};
const INCENDIO_SUB_COPERTURE = buildIncendioSubCoperture();

// ─────────────────────────────────────────────────────────────────────────
// Sub-coperture FURTO per-immobile (settore-bound)
// Civ/ARC/Agr §3.6.3 (9) + CPI/RV §3.6.4 (8)
// ─────────────────────────────────────────────────────────────────────────

const buildFurtoSubCop = () => {
  const L = WIZARD_LABELS.furto;
  const T = FIELD_TOOLTIPS;
  return [
    { code: "I_cassaforte", label: L.sub_coperture_civ_agr_arc.I_cassaforte,
      type: "value_eur", hint: L.sub_hints.I_cassaforte,
      placeholder: "Valore PRA €", rate_permille: 10.0,
      tooltip: T.furto_I_cassaforte },
    { code: "D_trasporto_valori",
      label: L.sub_coperture_civ_agr_arc.D_trasporto_valori,
      type: "value_eur", hint: L.sub_hints.D_trasporto_valori,
      placeholder: "Valore PRA €", rate_permille: 20.0,
      tooltip: T.furto_D_trasporto_valori },
    { code: "A_archivi", label: L.sub_coperture_civ_agr_arc.A_archivi,
      type: "value_eur", hint: L.sub_hints.A_archivi,
      placeholder: "Valore PRA €", rate_permille: 5.0,
      tooltip: T.furto_A_archivi },
    { code: "B_scippo_generico",
      label: L.sub_coperture_civ_agr_arc.B_scippo_generico,
      type: "toggle", hint: L.sub_hints.B_scippo_generico },
    { code: "C_scippo_albergo",
      label: L.sub_coperture_civ_agr_arc.C_scippo_albergo,
      type: "toggle", hint: L.sub_hints.C_scippo_albergo,
      sectorBound: "ARC alberghi" },
    { code: "E_bestiame_aperto",
      label: L.sub_coperture_civ_agr_arc.E_bestiame_aperto,
      type: "value_eur", hint: L.sub_hints.E_bestiame_aperto,
      sectorBound: "Agricolo" },
    { code: "F_macchine_aperto",
      label: L.sub_coperture_civ_agr_arc.F_macchine_aperto,
      type: "value_eur", hint: L.sub_hints.F_macchine_aperto,
      sectorBound: "Agricolo" },
    { code: "G_veicoli_albergo",
      label: L.sub_coperture_civ_agr_arc.G_veicoli_albergo,
      type: "value_eur", hint: L.sub_hints.G_veicoli_albergo,
      sectorBound: "ARC alberghi", rate_permille: 19.0,
      tooltip: T.furto_G_veicoli_albergo },
    { code: "H_allarme", label: L.sub_coperture_civ_agr_arc.H_allarme,
      type: "toggle", hint: L.sub_hints.H_allarme },
  ];
};
const FURTO_SUB_COPERTURE = buildFurtoSubCop();

const FURTO_FORMA_OPTIONS = [
  { value: "primo_rischio_assoluto", label: WIZARD_LABELS.furto.forme.pra },
  { value: "valore_intero", label: WIZARD_LABELS.furto.forme.valore_intero },
  { value: "globale", label: WIZARD_LABELS.furto.forme.globale },
  { value: "analitica", label: WIZARD_LABELS.furto.forme.analitica },
];

// ─────────────────────────────────────────────────────────────────────────
// RCF — opzioni dropdown
// ─────────────────────────────────────────────────────────────────────────

const RCF_CODICI_OPTIONS = Object.entries(
  WIZARD_LABELS.rcf.codici,
).map(([value, label]) => ({ value, label: `${value} — ${label}` }));

const RCF_MASSIMALI_OPTIONS = Object.entries(
  WIZARD_LABELS.rcf.massimali,
).map(([value, label]) => ({ value: Number(value), label }));

const RCF_AUTO_SUGGEST_BY_SETTORE = {
  civile_agricolo: "20111",
  altri_rischi_civili: "20113",
  commercio_piccola_industria: "20115",
  rischi_vari: "20117",
};

// ─────────────────────────────────────────────────────────────────────────
// ATMOSFERICI — sub-coperture per-immobile I/L/M/N/O/P/Q/Z
// ─────────────────────────────────────────────────────────────────────────

const buildAtmosfericiSubCop = () => {
  const L = WIZARD_LABELS.atmosferici;
  return [
    { code: "I_vento_grandine", label: L.sub_coperture.I_vento_grandine,
      type: "toggle", hint: L.sub_hints.I_vento_grandine },
    { code: "L_vg_fragili", label: L.sub_coperture.L_vg_fragili,
      type: "toggle", hint: L.sub_hints.L_vg_fragili },
    { code: "M_acqua_piovana", label: L.sub_coperture.M_acqua_piovana,
      type: "limit_select", hint: L.sub_hints.M_acqua_piovana,
      options: L.limit_options_m_q },
    { code: "N_spargimenti_acqua",
      label: L.sub_coperture.N_spargimenti_acqua,
      type: "toggle", hint: L.sub_hints.N_spargimenti_acqua },
    { code: "O_gelo", label: L.sub_coperture.O_gelo,
      type: "limit_select", hint: L.sub_hints.O_gelo,
      options: L.limit_options_o },
    { code: "P_ricerca_guasti", label: L.sub_coperture.P_ricerca_guasti,
      type: "toggle", hint: L.sub_hints.P_ricerca_guasti },
    { code: "Q_dispersione", label: L.sub_coperture.Q_dispersione,
      type: "limit_select", hint: L.sub_hints.Q_dispersione,
      options: L.limit_options_m_q },
    { code: "Z_sovraccarico_neve",
      label: L.sub_coperture.Z_sovraccarico_neve,
      type: "toggle", hint: L.sub_hints.Z_sovraccarico_neve },
  ];
};
const ATMOSFERICI_SUB_COPERTURE = buildAtmosfericiSubCop();

// ─────────────────────────────────────────────────────────────────────────
// CATASTROFALI — sub-coperture per-immobile J/K con auth flag + tasso manuale
// ─────────────────────────────────────────────────────────────────────────

const buildCatastrofaliSubCop = () => {
  const L = WIZARD_LABELS.catastrofali;
  const T = FIELD_TOOLTIPS;
  const extraFields = (ucfg, patchFn) => (
    <div className="flex flex-col gap-2 mt-2">
      <input
        type="number"
        min="0"
        placeholder="Valore PRA € (somma assicurata)"
        value={ucfg.valore_pra_eur ?? ""}
        onChange={(e) =>
          patchFn({
            valore_pra_eur: e.target.value === ""
              ? 0 : Number(e.target.value),
          })}
        className="px-2 py-1.5 text-xs rounded border w-48"
        style={{ borderColor: "#E5DFD4" }}
      />
      <label className="text-xs flex items-center gap-2 cursor-pointer select-none"
              style={{ color: "#3A3A3A" }}>
        <button
          type="button"
          role="checkbox"
          aria-checked={!!ucfg.autorizzazione_ottenuta}
          onClick={() => patchFn({
            autorizzazione_ottenuta: !ucfg.autorizzazione_ottenuta })}
          className="inline-flex items-center justify-center transition"
          style={{
            width: 22, height: 22, minWidth: 22, minHeight: 22,
            borderRadius: 5,
            border: `2px solid ${ucfg.autorizzazione_ottenuta
              ? "#C9A961" : "#0B2545"}`,
            background: ucfg.autorizzazione_ottenuta
              ? "#C9A961" : "#FFF",
            cursor: "pointer", flexShrink: 0,
          }}
        >
          {ucfg.autorizzazione_ottenuta && (
            <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
              <path d="M4 10.5L8.5 15L16 6" stroke="#06182E"
                     strokeWidth="3.2" strokeLinecap="round"
                     strokeLinejoin="round" />
            </svg>
          )}
        </button>
        <span>Autorizzazione direzionale ottenuta?</span>
      </label>
      {ucfg.autorizzazione_ottenuta && (
        <input
          type="number"
          step="0.01"
          min="0"
          placeholder="Tasso negoziato ‰"
          value={ucfg.tasso_negoziato_permille ?? ""}
          onChange={(e) =>
            patchFn({
              tasso_negoziato_permille: e.target.value === ""
                ? null : Number(e.target.value),
            })}
          className="px-2 py-1.5 text-xs rounded border w-36"
          style={{ borderColor: "#E5DFD4" }}
        />
      )}
    </div>
  );
  return [
    { code: "J_terremoto", label: L.sub_coperture.J_terremoto,
      type: "toggle", hint: L.sub_hints.J_terremoto,
      manualInput: true, extraFields, tooltip: T.J_terremoto },
    { code: "K_inondazione", label: L.sub_coperture.K_inondazione,
      type: "toggle", hint: L.sub_hints.K_inondazione,
      manualInput: true, extraFields, tooltip: T.K_inondazione },
  ];
};
const CATASTROFALI_SUB_COPERTURE = buildCatastrofaliSubCop();

// ─────────────────────────────────────────────────────────────────────────
// ALTRE — sub-coperture per-immobile G/H/R/S/T/MC
// ─────────────────────────────────────────────────────────────────────────

const buildAltreSubCop = () => {
  const L = WIZARD_LABELS.altre;
  return [
    { code: "G_sociopolitici", label: L.sub_coperture.G_sociopolitici,
      type: "toggle", hint: L.sub_hints.G_sociopolitici },
    { code: "H_terrorismo", label: L.sub_coperture.H_terrorismo,
      type: "toggle", hint: L.sub_hints.H_terrorismo },
    { code: "R_refrigerazione", label: L.sub_coperture.R_refrigerazione,
      type: "value_eur", hint: L.sub_hints.R_refrigerazione,
      manualInput: true, placeholder: "Premio quotato €" },
    { code: "S_folgorazione_bestiame",
      label: L.sub_coperture.S_folgorazione_bestiame,
      type: "value_eur", hint: L.sub_hints.S_folgorazione_bestiame,
      sectorBound: "Agricolo", placeholder: "Valore bestiame €" },
    { code: "T_fermentazione_foraggio",
      label: L.sub_coperture.T_fermentazione_foraggio,
      type: "value_eur", hint: L.sub_hints.T_fermentazione_foraggio,
      sectorBound: "Agricolo", placeholder: "Valore foraggio €" },
    { code: "MC_maggiori_costi", label: L.sub_coperture.MC_maggiori_costi,
      type: "value_eur", hint: L.sub_hints.MC_maggiori_costi,
      manualInput: true, placeholder: "Premio quotato €",
      tooltip: FIELD_TOOLTIPS.MC_maggiori_costi },
  ];
};
const ALTRE_SUB_COPERTURE = buildAltreSubCop();

const STEPS = [
  { key: "contraente", label: "Contraente", icon: ListChecks },
  { key: "incendio", label: "Incendio", icon: Flame },
  { key: "furto", label: "Furto", icon: ShieldAlert },
  { key: "rcf", label: "RC Fabbricato", icon: Building },
  { key: "atmosferici", label: "Atmosferici", icon: CloudRain },
  { key: "catastrofali", label: "Catastrofali", icon: Mountain },
  { key: "altre", label: "Altre", icon: Sparkles },
  { key: "riepilogo", label: "Riepilogo & Submit", icon: Check },
];

// ─────────────────────────────────────────────────────────────────────────

export default function GaranzieWizardBulk({ token, onSubmitted, onBack }) {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [anagrafica, setAnagrafica] = useState([]);
  const [bulk, setBulk] = useState(emptyBulk());
  const [stepIdx, setStepIdx] = useState(0);
  const [saving, setSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const saveTimer = useRef(null);

  // Carica state iniziale
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(
          `${API}/public/quoter/invites/${token}/garanzie-state`);
        if (cancelled) return;
        setAnagrafica(r.data.anagrafica || []);
        const saved = r.data.step2_data || {};
        if (saved.schema_version === "bulk_v1") {
          // Hydrate da bulk_v1 esistente — merge con default per chiavi mancanti
          const def = emptyBulk();
          setBulk({
            ...def, ...saved,
            contraente: { ...def.contraente, ...(saved.contraente || {}) },
            incendio: {
              ...def.incendio, ...(saved.incendio || {}),
              sub_coperture_globali: {
                ...def.incendio.sub_coperture_globali,
                ...((saved.incendio || {}).sub_coperture_globali || {}),
              },
            },
            furto: { ...def.furto, ...(saved.furto || {}) },
            rcf: { ...def.rcf, ...(saved.rcf || {}) },
            eventi_atmosferici: {
              ...def.eventi_atmosferici,
              ...(saved.eventi_atmosferici || {}),
            },
            eventi_catastrofali: {
              ...def.eventi_catastrofali,
              ...(saved.eventi_catastrofali || {}),
            },
            altre_sub_coperture: {
              ...def.altre_sub_coperture,
              ...(saved.altre_sub_coperture || {}),
            },
          });
          if (Number.isInteger(saved.step_index)) setStepIdx(saved.step_index);
        }
      } catch (e) {
        toast.error("Errore caricamento wizard");
        console.error(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  // Autosave (debounced 1.5s)
  const scheduleSave = useCallback((newBulk, newIdx) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      setSaving(true);
      try {
        const payload = { ...newBulk, step_index: newIdx ?? stepIdx };
        const r = await axios.post(
          `${API}/public/quoter/invites/${token}/garanzie-save-step`,
          payload,
        );
        setLastSavedAt(new Date(r.data.saved_at));
      } catch (e) {
        console.error("autosave error", e);
      } finally {
        setSaving(false);
      }
    }, 1500);
  }, [stepIdx, token]);

  const updateBulk = useCallback((updater) => {
    setBulk((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      scheduleSave(next);
      return next;
    });
  }, [scheduleSave]);

  const goToStep = (idx) => {
    setStepIdx(idx);
    scheduleSave(bulk, idx);
  };

  // ── Step Incendio handlers ──────────────────────────────────────────
  const incendioColumns = useMemo(() => [
    {
      key: "valore_fabbricato_eur",
      label: WIZARD_LABELS.incendio.columns.valore_fabbricato_eur,
      type: "number", required: true, minWidth: 160,
      tooltip: FIELD_TOOLTIPS.valore_fabbricato_eur,
    },
    {
      key: "valore_contenuto_eur",
      label: WIZARD_LABELS.incendio.columns.valore_contenuto_eur,
      type: "number", minWidth: 160,
      tooltip: FIELD_TOOLTIPS.valore_contenuto_eur,
    },
  ], []);

  const handleIncendioSelection = (newIds) => {
    updateBulk((p) => {
      const perImm = { ...p.incendio.per_immobile };
      // Pre-fill valore fabbricato da anagrafica quando si seleziona
      newIds.forEach((uid) => {
        if (!perImm[uid]) {
          const ana = anagrafica.find((m) => m.ubicazione_id === uid);
          perImm[uid] = {
            valore_fabbricato_eur: ana?.valore_fabbricato_eur || 0,
            valore_contenuto_eur: 0,
          };
        }
      });
      return { ...p, incendio: {
        ...p.incendio, immobili_inclusi: newIds, per_immobile: perImm,
      }};
    });
  };
  const handleIncendioValue = (uid, key, value) => {
    updateBulk((p) => ({
      ...p, incendio: {
        ...p.incendio,
        per_immobile: {
          ...p.incendio.per_immobile,
          [uid]: { ...(p.incendio.per_immobile[uid] || {}), [key]: value },
        },
      },
    }));
  };
  const handleIncendioSubCop = (code, payload) => {
    updateBulk((p) => ({
      ...p, incendio: {
        ...p.incendio,
        sub_coperture_globali: {
          ...p.incendio.sub_coperture_globali,
          [code]: payload,
        },
      },
    }));
  };

  // ── Helper generico: handler per garanzie con immobili_inclusi +
  //    per_immobile + sub_coperture_per_immobile (Furto, RCF, Atm, Cat, Altre)
  const makeBulkHandlers = (sectionKey, opts = {}) => {
    const { defaultPerImmobile, subCopKey = "sub_coperture_per_immobile" } =
      opts;
    return {
      onSelection: (newIds) => {
        updateBulk((p) => {
          const sec = p[sectionKey] || {};
          const perImm = { ...(sec.per_immobile || {}) };
          if (defaultPerImmobile) {
            newIds.forEach((uid) => {
              if (!perImm[uid]) {
                const ana = anagrafica.find((m) =>
                  m.ubicazione_id === uid);
                perImm[uid] = defaultPerImmobile(ana);
              }
            });
          }
          return { ...p, [sectionKey]: {
            ...sec, immobili_inclusi: newIds, per_immobile: perImm,
          }};
        });
      },
      onValue: (uid, key, value) => {
        updateBulk((p) => ({
          ...p, [sectionKey]: {
            ...p[sectionKey],
            per_immobile: {
              ...(p[sectionKey].per_immobile || {}),
              [uid]: {
                ...((p[sectionKey].per_immobile || {})[uid] || {}),
                [key]: value,
              },
            },
          },
        }));
      },
      onSubCop: (code, payload, uid) => {
        updateBulk((p) => {
          const sec = p[sectionKey] || {};
          const subs = { ...(sec[subCopKey] || {}) };
          if (uid) {
            subs[code] = { ...(subs[code] || {}), [uid]: payload };
          } else {
            subs[code] = payload;
          }
          return { ...p, [sectionKey]: { ...sec, [subCopKey]: subs } };
        });
      },
    };
  };

  // ── Step Furto handlers
  const furtoColumns = useMemo(() => [
    { key: "forma",
      label: WIZARD_LABELS.furto.columns.forma,
      type: "select", required: true, minWidth: 220,
      options: FURTO_FORMA_OPTIONS,
      tooltip: FIELD_TOOLTIPS.forma_furto },
    { key: "valore_eur",
      label: WIZARD_LABELS.furto.columns.valore_eur,
      type: "number", required: true, minWidth: 160,
      tooltip: FIELD_TOOLTIPS.valore_furto_pra },
  ], []);
  const furtoH = useMemo(() => makeBulkHandlers("furto", {
    defaultPerImmobile: () => ({
      forma: "primo_rischio_assoluto", valore_eur: 0,
    }),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [anagrafica]);

  // ── Step RCF handlers (auto-suggest codice dal settore anagrafica)
  const rcfColumns = useMemo(() => [
    { key: "codice", label: WIZARD_LABELS.rcf.columns.codice,
      type: "select", required: true, minWidth: 360,
      options: RCF_CODICI_OPTIONS,
      tooltip: FIELD_TOOLTIPS.codice_rcf },
    { key: "massimale_eur",
      label: WIZARD_LABELS.rcf.columns.massimale_eur,
      type: "select", required: true, minWidth: 160,
      options: RCF_MASSIMALI_OPTIONS,
      tooltip: FIELD_TOOLTIPS.massimale_rcf },
    { key: "full",
      label: WIZARD_LABELS.rcf.columns.full,
      type: "toggle", minWidth: 80 },
  ], []);
  const rcfH = useMemo(() => makeBulkHandlers("rcf", {
    defaultPerImmobile: (ana) => ({
      codice: ana?.settore
        ? (RCF_AUTO_SUGGEST_BY_SETTORE[ana.settore] || "20115") : "20115",
      massimale_eur: 1000000, full: false,
    }),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [anagrafica]);

  // ── Step Atmosferici/Catastrofali/Altre handlers (solo sub-cop)
  const atmH = useMemo(() => makeBulkHandlers("eventi_atmosferici"),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []);
  const catH = useMemo(() => makeBulkHandlers("eventi_catastrofali"),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []);
  const altreH = useMemo(() => makeBulkHandlers("altre_sub_coperture"),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []);

  // ── Render ───────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center gap-3 py-12 justify-center text-sm"
            style={{ color: PALETTE.muted }}
            data-testid="wizard-bulk-loading">
        <Loader2 className="animate-spin" size={18} /> Caricamento wizard…
      </div>
    );
  }

  const currentStep = STEPS[stepIdx];

  return (
    <div data-testid="wizard-bulk-root" className="space-y-4">
      {/* Header steps */}
      <div className="rounded-lg p-3 flex items-center gap-1 overflow-x-auto"
            style={{ background: "#FFF", border: `1px solid ${PALETTE.border}` }}>
        {STEPS.map((s, i) => {
          const Icon = s.icon;
          const done = i < stepIdx;
          const active = i === stepIdx;
          return (
            <button
              type="button"
              key={s.key}
              data-testid={`wizard-step-tab-${s.key}`}
              onClick={() => goToStep(i)}
              className="flex items-center gap-2 px-3 py-2 rounded text-xs whitespace-nowrap transition"
              style={{
                background: active ? PALETTE.navy
                  : done ? PALETTE.goldSoft : "transparent",
                color: active ? "#FFF" : PALETTE.navyDark,
                fontWeight: active ? 600 : 400,
                border: active ? "none" : `1px solid ${PALETTE.border}`,
              }}
            >
              {done ? <Check size={14} /> : <Icon size={14} />}
              {s.label}
            </button>
          );
        })}
      </div>

      {/* Status bar */}
      <div
        className="flex items-center justify-between text-xs"
        style={{ color: PALETTE.muted }}
      >
        <div>
          Step {stepIdx + 1} di {STEPS.length} — {currentStep.label}
        </div>
        <div className="flex items-center gap-3">
          {saving ? (
            <span className="flex items-center gap-1">
              <Loader2 className="animate-spin" size={12} /> Salvataggio…
            </span>
          ) : lastSavedAt ? (
            <span className="flex items-center gap-1">
              <Save size={12} /> Salvato {lastSavedAt.toLocaleTimeString("it-IT")}
            </span>
          ) : null}
          <span data-testid="wizard-version">v1.14.61.3.0 (bulk_v1)</span>
        </div>
      </div>

      {/* v1.14.61.2.0 — Layout senza sidebar: il cliente non vede più
          il premio live (broker-gated quote). */}
      <div className="block">
        <div className="min-w-0">
        {currentStep.key === "contraente" && (
        <ContraenteStepInline bulk={bulk} updateBulk={updateBulk} />
      )}

      {currentStep.key === "incendio" && (
        <GaranziaTableStep
          title={WIZARD_LABELS.incendio.title}
          subtitle={WIZARD_LABELS.incendio.subtitle}
          icon={Flame}
          immobili={anagrafica}
          columns={incendioColumns}
          selectedIds={bulk.incendio.immobili_inclusi}
          perImmobile={bulk.incendio.per_immobile}
          onSelectionChange={handleIncendioSelection}
          onValueChange={handleIncendioValue}
          subCoperture={INCENDIO_SUB_COPERTURE}
          subCopMode="global"
          subCopValues={bulk.incendio.sub_coperture_globali}
          onSubCopChange={handleIncendioSubCop}
          selectAllLabel="Includi tutti gli immobili"
        />
      )}

      {currentStep.key === "furto" && (
        <GaranziaTableStep
          title={WIZARD_LABELS.furto.title}
          subtitle={WIZARD_LABELS.furto.subtitle}
          icon={ShieldAlert}
          immobili={anagrafica}
          columns={furtoColumns}
          selectedIds={bulk.furto.immobili_inclusi}
          perImmobile={bulk.furto.per_immobile}
          onSelectionChange={furtoH.onSelection}
          onValueChange={furtoH.onValue}
          subCoperture={FURTO_SUB_COPERTURE}
          subCopMode="per_immobile"
          subCopValues={bulk.furto.sub_coperture_per_immobile}
          onSubCopChange={furtoH.onSubCop}
          selectAllLabel="Includi tutti gli immobili"
        />
      )}

      {currentStep.key === "rcf" && (
        <GaranziaTableStep
          title={WIZARD_LABELS.rcf.title}
          subtitle={WIZARD_LABELS.rcf.subtitle}
          icon={Building}
          immobili={anagrafica}
          columns={rcfColumns}
          selectedIds={bulk.rcf.immobili_inclusi}
          perImmobile={bulk.rcf.per_immobile}
          onSelectionChange={rcfH.onSelection}
          onValueChange={rcfH.onValue}
          selectAllLabel="Includi tutti gli immobili"
        />
      )}

      {currentStep.key === "atmosferici" && (
        <GaranziaTableStep
          title={WIZARD_LABELS.atmosferici.title}
          subtitle={WIZARD_LABELS.atmosferici.subtitle}
          icon={CloudRain}
          immobili={anagrafica}
          columns={[]}
          selectedIds={bulk.eventi_atmosferici.immobili_inclusi}
          perImmobile={{}}
          onSelectionChange={atmH.onSelection}
          onValueChange={atmH.onValue}
          subCoperture={ATMOSFERICI_SUB_COPERTURE}
          subCopMode="per_immobile"
          subCopValues={bulk.eventi_atmosferici.sub_coperture_per_immobile}
          onSubCopChange={atmH.onSubCop}
          selectAllLabel="Includi tutti gli immobili"
        />
      )}

      {currentStep.key === "catastrofali" && (
        <GaranziaTableStep
          title={WIZARD_LABELS.catastrofali.title}
          subtitle={WIZARD_LABELS.catastrofali.subtitle}
          icon={Mountain}
          immobili={anagrafica}
          columns={[]}
          selectedIds={bulk.eventi_catastrofali.immobili_inclusi}
          perImmobile={{}}
          onSelectionChange={catH.onSelection}
          onValueChange={catH.onValue}
          subCoperture={CATASTROFALI_SUB_COPERTURE}
          subCopMode="per_immobile"
          subCopValues={bulk.eventi_catastrofali.sub_coperture_per_immobile}
          onSubCopChange={catH.onSubCop}
          rdWarning={WIZARD_LABELS.catastrofali.rd_warning}
          selectAllLabel="Includi tutti gli immobili"
        />
      )}

      {currentStep.key === "altre" && (
        <GaranziaTableStep
          title={WIZARD_LABELS.altre.title}
          subtitle={WIZARD_LABELS.altre.subtitle}
          icon={Sparkles}
          immobili={anagrafica}
          columns={[]}
          selectedIds={bulk.altre_sub_coperture.immobili_inclusi}
          perImmobile={{}}
          onSelectionChange={altreH.onSelection}
          onValueChange={altreH.onValue}
          subCoperture={ALTRE_SUB_COPERTURE}
          subCopMode="per_immobile"
          subCopValues={bulk.altre_sub_coperture.sub_coperture_per_immobile}
          onSubCopChange={altreH.onSubCop}
          selectAllLabel="Includi tutti gli immobili"
        />
      )}

      {currentStep.key === "riepilogo" && (
        <RiepilogoStep
          token={token}
          bulk={bulk}
          anagrafica={anagrafica}
          onSubmitted={onSubmitted}
        />
      )}
        </div>
        {/* v1.14.61.2.0 — Sidebar live premium rimossa: il cliente non
            vede più il premio finché il broker non trasmette. */}
      </div>

      {/* Nav buttons */}
      <div className="flex items-center justify-between pt-4">
        <button
          type="button"
          data-testid="wizard-back-btn"
          onClick={() => stepIdx === 0
            ? onBack && onBack() : goToStep(stepIdx - 1)}
          disabled={submitting}
          className="px-4 py-2 rounded border text-sm flex items-center gap-2"
          style={{
            borderColor: PALETTE.border, color: PALETTE.navyDark,
            background: "#FFF",
          }}
        >
          <ArrowLeft size={14} />
          {stepIdx === 0 ? "Torna ad Anagrafica" : "Indietro"}
        </button>
        {stepIdx < STEPS.length - 1 ? (
          <button
            type="button"
            data-testid="wizard-next-btn"
            onClick={() => goToStep(stepIdx + 1)}
            disabled={submitting}
            className="px-4 py-2 rounded text-sm flex items-center gap-2"
            style={{ background: PALETTE.navy, color: "#FFF" }}
          >
            Avanti <ArrowRight size={14} />
          </button>
        ) : (
          <span className="text-xs" style={{ color: PALETTE.muted }}>
            Conferma il preventivo dalla card sopra ↑
          </span>
        )}
      </div>
    </div>
  );
}

function ContraenteStepInline({ bulk, updateBulk }) {
  const c = bulk.contraente;
  const set = (patch) =>
    updateBulk((p) => ({ ...p, contraente: { ...p.contraente, ...patch } }));
  return (
    <section
      data-testid="step-contraente"
      className="rounded-lg p-5"
      style={{ background: "#FFF", border: `1px solid ${PALETTE.border}` }}
    >
      <h2 className="text-lg font-semibold mb-3"
           style={{ color: PALETTE.navyDark }}>
        Dati del contraente
      </h2>
      <div className="grid sm:grid-cols-2 gap-3">
        <input
          data-testid="contraente-ragione-sociale"
          value={c.ragione_sociale || ""}
          onChange={(e) => set({ ragione_sociale: e.target.value })}
          placeholder="Ragione sociale / Nome cliente *"
          className="px-3 py-2 rounded border text-sm"
          style={{ borderColor: PALETTE.border }}
        />
        <input
          data-testid="contraente-piva"
          value={c.partita_iva || ""}
          onChange={(e) => set({ partita_iva: e.target.value })}
          placeholder="P.IVA / CF"
          className="px-3 py-2 rounded border text-sm"
          style={{ borderColor: PALETTE.border }}
        />
        <input
          data-testid="contraente-email"
          type="email"
          value={c.email || ""}
          onChange={(e) => set({ email: e.target.value })}
          placeholder="Email *"
          className="px-3 py-2 rounded border text-sm"
          style={{ borderColor: PALETTE.border }}
        />
        <input
          data-testid="contraente-telefono"
          value={c.telefono || ""}
          onChange={(e) => set({ telefono: e.target.value })}
          placeholder="Telefono"
          className="px-3 py-2 rounded border text-sm"
          style={{ borderColor: PALETTE.border }}
        />
        <textarea
          data-testid="contraente-note"
          value={c.note || ""}
          onChange={(e) => set({ note: e.target.value })}
          placeholder="Note libere"
          rows={2}
          className="px-3 py-2 rounded border text-sm sm:col-span-2"
          style={{ borderColor: PALETTE.border }}
        />
      </div>
    </section>
  );
}
